/**
 * EmailFilterDialog — E-Mail-Filter verwalten
 * 
 * Professionelle Filterverwaltung wie in großen E-Mail-Clients:
 * - Filter erstellen/bearbeiten/löschen
 * - Bedingungen: Von, An, Betreff, Inhalt, Anhang
 * - Aktionen: Verschieben, Als gelesen markieren, Markieren, Weiterleiten
 */

import { useState, useEffect } from 'react';
import {
  X, Plus, Trash2, Save, Filter, ArrowRight,
  Mail, User, FileText, Paperclip, Star,
  FolderInput, Eye, Forward, Tag, Loader2,
  ChevronDown, ChevronUp, GripVertical, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { toast } from 'sonner';
import type { EmailFilter } from '@/hooks/useEmailAccounts';

const FOLDER_OPTIONS = [
  { id: 'INBOX', label: 'Posteingang' },
  { id: 'SENT', label: 'Gesendet' },
  { id: 'STARRED', label: 'Markiert' },
  { id: 'TRASH', label: 'Papierkorb' },
  { id: 'ARCHIVE', label: 'Archiv' },
  { id: 'SPAM', label: 'Spam' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  filters: EmailFilter[];
  accountId: string;
  onSave: (filter: EmailFilter) => Promise<any>;
  onDelete: (filterId: string) => Promise<void>;
}

export default function EmailFilterDialog({ open, onClose, filters, accountId, onSave, onDelete }: Props) {
  const [editingFilter, setEditingFilter] = useState<EmailFilter | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  if (!open) return null;

  const createNewFilter = (): EmailFilter => ({
    account_id: accountId,
    name: `Filter ${filters.length + 1}`,
    is_active: true,
    priority: filters.length,
    condition_from: '',
    condition_to: '',
    condition_subject: '',
    condition_contains: '',
    condition_has_attachment: undefined,
    action_move_to: '',
    action_mark_read: false,
    action_star: false,
    action_forward_to: '',
    action_label: '',
  });

  const handleSave = async () => {
    if (!editingFilter) return;
    if (!editingFilter.name.trim()) {
      toast.error('Bitte einen Filternamen eingeben');
      return;
    }
    // Mindestens eine Bedingung
    const hasCondition = editingFilter.condition_from || editingFilter.condition_to ||
      editingFilter.condition_subject || editingFilter.condition_contains ||
      editingFilter.condition_has_attachment;
    if (!hasCondition) {
      toast.error('Bitte mindestens eine Bedingung festlegen');
      return;
    }
    // Mindestens eine Aktion
    const hasAction = editingFilter.action_move_to || editingFilter.action_mark_read ||
      editingFilter.action_star || editingFilter.action_forward_to || editingFilter.action_label;
    if (!hasAction) {
      toast.error('Bitte mindestens eine Aktion festlegen');
      return;
    }

    setSaving(true);
    try {
      await onSave(editingFilter);
      toast.success('Filter gespeichert');
      setEditingFilter(null);
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Speichern');
    }
    setSaving(false);
  };

  const handleDelete = async (filterId: string) => {
    setDeleting(filterId);
    try {
      await onDelete(filterId);
      toast.success('Filter gelöscht');
      if (editingFilter?.id === filterId) setEditingFilter(null);
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Löschen');
    }
    setDeleting(null);
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl rounded-2xl max-h-[85vh] flex flex-col shadow-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <Filter className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">E-Mail-Filter</h3>
              <p className="text-xs text-gray-500">{filters.length} Filter aktiv</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {editingFilter ? (
            /* ─── Filter bearbeiten ─── */
            <div className="p-5 space-y-5">
              <div className="flex items-center justify-between">
                <button onClick={() => setEditingFilter(null)} className="text-sm text-blue-600 hover:underline font-medium">
                  ← Zurück zur Liste
                </button>
                <span className="text-xs text-gray-400">
                  {editingFilter.id ? 'Filter bearbeiten' : 'Neuer Filter'}
                </span>
              </div>

              {/* Name */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Filtername</label>
                <input
                  value={editingFilter.name}
                  onChange={e => setEditingFilter({ ...editingFilter, name: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:border-blue-400 outline-none"
                  placeholder="z.B. Rechnungen automatisch archivieren"
                />
              </div>

              {/* Aktiv-Toggle */}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-gray-700">Filter aktiv</span>
                <button
                  onClick={() => setEditingFilter({ ...editingFilter, is_active: !editingFilter.is_active })}
                  className={`w-10 h-5.5 rounded-full transition-colors relative ${
                    editingFilter.is_active ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div className={`w-4.5 h-4.5 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform ${
                    editingFilter.is_active ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Bedingungen */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 block">
                  Bedingungen (wenn...)
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600 w-20 flex-shrink-0">Von:</span>
                    <input
                      value={editingFilter.condition_from || ''}
                      onChange={e => setEditingFilter({ ...editingFilter, condition_from: e.target.value })}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-blue-400 outline-none"
                      placeholder="z.B. rechnung@firma.de"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600 w-20 flex-shrink-0">An:</span>
                    <input
                      value={editingFilter.condition_to || ''}
                      onChange={e => setEditingFilter({ ...editingFilter, condition_to: e.target.value })}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-blue-400 outline-none"
                      placeholder="z.B. info@mietgo.de"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600 w-20 flex-shrink-0">Betreff:</span>
                    <input
                      value={editingFilter.condition_subject || ''}
                      onChange={e => setEditingFilter({ ...editingFilter, condition_subject: e.target.value })}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-blue-400 outline-none"
                      placeholder="z.B. Rechnung, Mahnung"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600 w-20 flex-shrink-0">Enthält:</span>
                    <input
                      value={editingFilter.condition_contains || ''}
                      onChange={e => setEditingFilter({ ...editingFilter, condition_contains: e.target.value })}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-blue-400 outline-none"
                      placeholder="Text im E-Mail-Body"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600 w-20 flex-shrink-0">Anhang:</span>
                    <select
                      value={editingFilter.condition_has_attachment === true ? 'yes' : editingFilter.condition_has_attachment === false ? 'no' : ''}
                      onChange={e => setEditingFilter({
                        ...editingFilter,
                        condition_has_attachment: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined,
                      })}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-blue-400 outline-none bg-white"
                    >
                      <option value="">Egal</option>
                      <option value="yes">Mit Anhang</option>
                      <option value="no">Ohne Anhang</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Aktionen */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 block">
                  Aktionen (dann...)
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <FolderInput className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600 w-24 flex-shrink-0">Verschieben:</span>
                    <select
                      value={editingFilter.action_move_to || ''}
                      onChange={e => setEditingFilter({ ...editingFilter, action_move_to: e.target.value })}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-blue-400 outline-none bg-white"
                    >
                      <option value="">— Nicht verschieben —</option>
                      {FOLDER_OPTIONS.map(f => (
                        <option key={f.id} value={f.id}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Als gelesen markieren</span>
                    </div>
                    <button
                      onClick={() => setEditingFilter({ ...editingFilter, action_mark_read: !editingFilter.action_mark_read })}
                      className={`w-9 h-5 rounded-full transition-colors relative ${
                        editingFilter.action_mark_read ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform ${
                        editingFilter.action_mark_read ? 'translate-x-4' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Markieren (Stern)</span>
                    </div>
                    <button
                      onClick={() => setEditingFilter({ ...editingFilter, action_star: !editingFilter.action_star })}
                      className={`w-9 h-5 rounded-full transition-colors relative ${
                        editingFilter.action_star ? 'bg-amber-500' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform ${
                        editingFilter.action_star ? 'translate-x-4' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Forward className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600 w-24 flex-shrink-0">Weiterleiten:</span>
                    <input
                      value={editingFilter.action_forward_to || ''}
                      onChange={e => setEditingFilter({ ...editingFilter, action_forward_to: e.target.value })}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-blue-400 outline-none"
                      placeholder="z.B. chef@firma.de"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600 w-24 flex-shrink-0">Label:</span>
                    <input
                      value={editingFilter.action_label || ''}
                      onChange={e => setEditingFilter({ ...editingFilter, action_label: e.target.value })}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-blue-400 outline-none"
                      placeholder="z.B. Wichtig, Buchhaltung"
                    />
                  </div>
                </div>
              </div>

              {/* Speichern */}
              <div className="flex items-center gap-3 pt-2">
                <button onClick={() => setEditingFilter(null)} className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  Abbrechen
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white text-sm font-bold px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Filter speichern
                </button>
              </div>
            </div>
          ) : (
            /* ─── Filter-Liste ─── */
            <div className="p-5">
              <button
                onClick={() => setEditingFilter(createNewFilter())}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors mb-4"
              >
                <Plus className="w-4 h-4" />
                Neuen Filter erstellen
              </button>

              {filters.length === 0 ? (
                <div className="text-center py-8">
                  <Filter className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 font-medium">Keine Filter vorhanden</p>
                  <p className="text-xs text-gray-400 mt-1">Erstelle Filter, um E-Mails automatisch zu sortieren.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filters.map(filter => (
                    <div
                      key={filter.id}
                      className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${filter.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{filter.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {[
                            filter.condition_from && `Von: ${filter.condition_from}`,
                            filter.condition_subject && `Betreff: ${filter.condition_subject}`,
                            filter.condition_contains && `Enthält: ${filter.condition_contains}`,
                          ].filter(Boolean).join(' • ') || 'Keine Bedingungen'}
                          {' → '}
                          {[
                            filter.action_move_to && `→ ${FOLDER_OPTIONS.find(f => f.id === filter.action_move_to)?.label || filter.action_move_to}`,
                            filter.action_mark_read && 'Gelesen',
                            filter.action_star && '⭐',
                            filter.action_forward_to && `→ ${filter.action_forward_to}`,
                          ].filter(Boolean).join(', ') || 'Keine Aktionen'}
                        </p>
                      </div>
                      <button
                        onClick={() => setEditingFilter(filter)}
                        className="text-xs text-blue-600 font-medium hover:underline opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => filter.id && handleDelete(filter.id)}
                        disabled={deleting === filter.id}
                        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      >
                        {deleting === filter.id ? (
                          <Loader2 className="w-3.5 h-3.5 text-red-500 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
